import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
  selectAvailableLDUsersToAdd,
  selectIsUpdatingLDTeamMembers,
} from 'state/LearningTeam/learningTeamSlice';
import { serializeAvailableLDUsersToAddForDropdown } from '../../utils';
import { UserOption } from 'utils/customTypes';
import { AddTeamMembersHandler } from 'types/store/learningTeam';

const DEBOUNCE_MS = 300;

export const useSearchMembers = (onAddUsers: AddTeamMembersHandler) => {
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const availableLDUsersToAdd = useSelector(selectAvailableLDUsersToAdd);
  const isUpdatingLDTeamMembers = useSelector(selectIsUpdatingLDTeamMembers);
  const availableLDUsersToAddForDropdown = useMemo(() => {
    return serializeAvailableLDUsersToAddForDropdown(availableLDUsersToAdd);
  }, [availableLDUsersToAdd]);

  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    return () => debouncedSetSearchTerm.cancel();
  }, [debouncedSetSearchTerm]);

  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm]
  );

  const handleSelectUsers = useCallback((users: UserOption[]) => {
    setSelectedUsers(users);
  }, []);

  const handleAddUsers = useCallback(async () => {
    const usersIds = selectedUsers.map((user) => user.value);
    const response = await onAddUsers(usersIds);
    if (response && Array.isArray(response.failedIds)) {
      setSelectedUsers((prev) =>
        prev.filter((user) => response.failedIds.includes(user.value))
      );
    }
  }, [selectedUsers, onAddUsers]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return availableLDUsersToAddForDropdown;
    const term = searchTerm.toLowerCase().trim();
    return availableLDUsersToAddForDropdown.filter((user) =>
      user.label?.toLowerCase().includes(term)
    );
  }, [availableLDUsersToAddForDropdown, searchTerm]);

  const canAddUsers = useMemo(() => {
    return selectedUsers.length > 0;
  }, [selectedUsers]);

  return {
    filteredUsers,
    handleSearchChange,
    handleSelectUsers,
    selectedUsers,
    canAddUsers,
    handleAddUsers,
    isUpdatingLDTeamMembers,
  };
};
